<template>
  <div
    class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#1E293B] p-5 sm:p-6 shadow-[0_1px_3px_rgba(0,0,0,0.03)]"
  >
    <!-- Header -->
    <div class="flex items-center justify-between mb-4 sm:mb-5">
      <div>
        <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
          Quick Launchpad
        </h3>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Frequently used management actions
        </p>
      </div>
      <div class="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
        <Sparkles class="w-4 h-4" />
      </div>
    </div>

    <!-- 2-column Grid of Action Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <!-- 1. Students -->
      <RouterLink
        to="/students"
        class="group flex items-center gap-3 p-3.5 rounded-xl border border-slate-200/70 dark:border-[#1E293B] hover:border-indigo-400 dark:hover:border-indigo-500/50 bg-slate-50/50 hover:bg-white dark:bg-[#0E1526]/60 dark:hover:bg-[#18243A] transition-all duration-200 shadow-2xs hover:shadow-md hover:shadow-indigo-500/5 hover:-translate-y-0.5"
      >
        <div
          class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/40 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
        >
          <Users class="w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            Students
          </p>
          <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            Rosters & profiles
          </p>
        </div>
        <ArrowUpRight class="w-4 h-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
      </RouterLink>

      <!-- 2. Attendance -->
      <RouterLink
        to="/attendance"
        class="group flex items-center gap-3 p-3.5 rounded-xl border border-slate-200/70 dark:border-[#1E293B] hover:border-indigo-400 dark:hover:border-indigo-500/50 bg-slate-50/50 hover:bg-white dark:bg-[#0E1526]/60 dark:hover:bg-[#18243A] transition-all duration-200 shadow-2xs hover:shadow-md hover:shadow-indigo-500/5 hover:-translate-y-0.5"
      >
        <div
          class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
        >
          <ClipboardCheck class="w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            Attendance
          </p>
          <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            Track daily check-in
          </p>
        </div>
        <ArrowUpRight class="w-4 h-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
      </RouterLink>

      <!-- 3. Teachers -->
      <RouterLink
        to="/teacher"
        class="group flex items-center gap-3 p-3.5 rounded-xl border border-slate-200/70 dark:border-[#1E293B] hover:border-indigo-400 dark:hover:border-indigo-500/50 bg-slate-50/50 hover:bg-white dark:bg-[#0E1526]/60 dark:hover:bg-[#18243A] transition-all duration-200 shadow-2xs hover:shadow-md hover:shadow-indigo-500/5 hover:-translate-y-0.5"
      >
        <div
          class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
        >
          <UserCheck class="w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            Faculty
          </p>
          <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            Teaching instructors
          </p>
        </div>
        <ArrowUpRight class="w-4 h-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
      </RouterLink>

      <!-- 4. Classes -->
      <RouterLink
        to="/classes"
        class="group flex items-center gap-3 p-3.5 rounded-xl border border-slate-200/70 dark:border-[#1E293B] hover:border-indigo-400 dark:hover:border-indigo-500/50 bg-slate-50/50 hover:bg-white dark:bg-[#0E1526]/60 dark:hover:bg-[#18243A] transition-all duration-200 shadow-2xs hover:shadow-md hover:shadow-indigo-500/5 hover:-translate-y-0.5"
      >
        <div
          class="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-950/60 text-violet-600 dark:text-violet-400 border border-violet-100 dark:border-violet-900/40 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
        >
          <School class="w-5 h-5" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
            Classes
          </p>
          <p class="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            Curriculum & sections
          </p>
        </div>
        <ArrowUpRight class="w-4 h-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
      </RouterLink>
    </div>
  </div>
</template>

<script setup>
  import {
    Users,
    UserCheck,
    ClipboardCheck,
    School,
    ArrowUpRight,
    Sparkles,
  } from "lucide-vue-next";
</script>
